import { useEffect, useState } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { EmailVerification } from "@/components/email-verification"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { GoogleAuthButton } from "@/components/google-auth-button"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { authClient } from "@/lib/auth-client"
import { parseVideoSourceSearch } from "@/lib/video-source"

export const Route = createFileRoute("/_auth/signup")({
  validateSearch: parseVideoSourceSearch,
  head: () => ({
    meta: [
      { title: "Create an account — intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SignUpPage,
})

function SignUpPage() {
  const navigate = useNavigate()
  const { source } = Route.useSearch()
  const { data: session } = authClient.useSession()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null
  )
  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (session)
      void navigate({
        to: "/app",
        search: source ? { source } : {},
        replace: true,
        reloadDocument: true,
      })
  }, [navigate, session, source])

  async function signUp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    try {
      const normalizedEmail = email.trim().toLowerCase()
      if (accountEmail !== normalizedEmail) {
        const result = await authClient.signUp.email({
          // Better Auth requires a name even though intoshort does not use one.
          name: normalizedEmail,
          email: normalizedEmail,
          password,
        })
        if (result.error) {
          setError(
            result.error.code === "INVALID_EMAIL"
              ? "This email address is invalid."
              : result.error.status === 422
                ? "An account with this email may already exist. Try signing in instead."
                : "Unable to create your account. Please try again."
          )
          return
        }
        setAccountEmail(normalizedEmail)
        setPassword("")
      }
      const delivery = await authClient.emailOtp.sendVerificationOtp({
        email: normalizedEmail,
        type: "email-verification",
      })
      if (delivery.error) {
        setError(
          delivery.error.code === "INVALID_EMAIL"
            ? "This email address is invalid."
            : "Unable to send your code. Wait a minute and try again. Your account does not need to be created again."
        )
        return
      }
      setVerificationEmail(normalizedEmail)
    } catch {
      setError(
        "Unable to reach the server. Check your connection and try again."
      )
    } finally {
      setPending(false)
    }
  }

  if (verificationEmail) {
    return (
      <main className="auth-main">
        <EmailVerification
          email={verificationEmail}
          source={source}
          onBack={() => {
            setVerificationEmail(null)
            setError(null)
          }}
        />
      </main>
    )
  }

  return (
    <main className="auth-main">
      <section className="auth-panel" aria-labelledby="sign-up-title">
        <p className="auth-kicker">Start creating</p>
        <h1 id="sign-up-title">Create your account</h1>
        <p className="auth-description">
          Continue with Google or create an account with your email.
        </p>
        <div className="auth-social">
          <GoogleAuthButton
            disabled={pending}
            onError={setError}
            callbackPath={
              source ? `/app?source=${encodeURIComponent(source)}` : "/app"
            }
          />
          <div className="auth-divider">
            <Separator aria-hidden="true" />
            <span>or</span>
            <Separator aria-hidden="true" />
          </div>
        </div>
        <form onSubmit={signUp} aria-busy={pending}>
          <FieldGroup>
            <Field
              data-disabled={pending}
              data-invalid={error === "This email address is invalid."}
            >
              <FieldLabel htmlFor="sign-up-email">Email</FieldLabel>
              <Input
                id="sign-up-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                  if (error === "This email address is invalid.") setError(null)
                }}
                disabled={pending}
                aria-invalid={error === "This email address is invalid."}
                aria-describedby={
                  error === "This email address is invalid."
                    ? "sign-up-error"
                    : undefined
                }
              />
            </Field>
            {accountEmail !== email.trim().toLowerCase() && (
              <Field data-disabled={pending}>
                <FieldLabel htmlFor="sign-up-password">Password</FieldLabel>
                <Input
                  id="sign-up-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={pending}
                  aria-describedby="sign-up-password-help"
                />
                <FieldDescription id="sign-up-password-help">
                  Use 8 to 128 characters.
                </FieldDescription>
              </Field>
            )}
            {error && (
              <p id="sign-up-error" className="auth-error" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" size="lg" disabled={pending}>
              {pending
                ? "Please wait…"
                : accountEmail === email.trim().toLowerCase()
                  ? "Send verification code"
                  : "Create account"}
            </Button>
          </FieldGroup>
        </form>
        <p className="auth-switch">
          Already have an account?{" "}
          <Link to="/signin" search={source ? { source } : {}} reloadDocument>
            Sign in
          </Link>
          .
        </p>
      </section>
    </main>
  )
}
